import sourceCode from "@/utils/code";

/**
 * Tuning surface for impression tracking. Kept as one frozen object so the knobs live in a
 * single, discoverable place (`impressionConfig.engagedWindowMs`) rather than scattered consts.
 */
const impressionConfig = {
  // Visibility: an item counts as visible at ≥50% of its own area OR ≥50% of the viewport.
  // Dense thresholds make the observer fire often as the ratio changes while scrolling.
  visibilityThreshold: 0.5,
  thresholds: Array.from({ length: 21 }, (_, i) => i / 20),

  // Episode lifecycle.
  flickerDebounceMs: 400, // leave + re-enter within this = same episode
  minDwellMs: 1000, // qualification floor for a status impression (on engaged dwell)
  minVideoDwellMs: 2000, // higher floor for a video view — a 1–2s glance isn't a view
  backstopMs: 600_000, // force-close an episode that never leaves the viewport

  // Engaged dwell: each activity credits the item as engaged for this long; windows merge,
  // idle beyond it is excluded (not capped).
  engagedWindowMs: 5000,

  // Fling gate: a view only qualifies once the item was on screen while NOT scrolling past it.
  // Scrolling faster than the threshold is a fling; those samples don't count as a read, and a
  // post flung past (never settled on) is dropped. When scrolling settles, a sweep re-credits
  // whatever is still on screen (the "post-scroll sweep").
  flingGateEnabled: true,
  flingVelocityPxPerMs: 2.0, // > this (≈2000 px/s) = fling, not reading
  scrollSettleMs: 150, // no scroll for this long = settled -> run the sweep

  // Queue + flush.
  flushMaxEvents: 50, // flush once the queue reaches this many
  flushIntervalMs: 30_000, // base cadence...
  flushBackoffMaxMs: 300_000, // ...backed off up to 5 min under repeated send failure
  flushJitterRatio: 0.2, // ±20% jitter so clients don't retry in lockstep against the server

  // Transport + persistence.
  queueMaxEvents: 500, // hard cap; oldest dropped past this
  persistDebounceMs: 2000, // coalesce enqueue-driven localStorage writes off the hot path
  eventTtlMs: 48 * 60 * 60 * 1000, // stale events dropped on hydrate
  maxEventsPerRequest: 50, // batch size per POST
  retryMaxAttempts: 3, // then left queued for the next trigger
  retryBaseMs: 4000, // exponential backoff: 4s, 8s, 16s
  storageKey: "truth:impressions:queue",

  // Session (per-tab identity).
  seenStatusCap: 5000, // distinct statuses remembered for first_impression (matches Android)
  sessionIdleMs: 30 * 60 * 1000, // a gap this long with no events starts a new session
  sessionStorageKey: "truth:impressions:session",
  // Cross-tab registry (localStorage, shared across tabs) of live sessions + their last activity,
  // read on load to mint deferred session_end events for tabs that closed without emitting one.
  sessionRegistryKey: "truth:analytics:sessions",

  appVersion: sourceCode.version,
} as const;

export { impressionConfig };
